import { Component } from "react";
import { Link } from "react-router-dom";

/**
 * ErrorBoundary — catches unhandled render errors in any child subtree.
 *
 * Usage:
 *   <ErrorBoundary section="AI News">
 *     <AINewsPage />
 *   </ErrorBoundary>
 *
 * Props:
 *   section  (string)  — label shown in the fallback UI
 *   fallback (node)    — optional custom fallback UI
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log to console in development; swap for Sentry/DataDog in production
    if (process.env.NODE_ENV !== "production") {
      console.error("[ErrorBoundary]", error, info.componentStack);
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    if (this.props.fallback) return this.props.fallback;

    const section = this.props.section || "this page";

    return (
      <div className="min-h-[400px] flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <p className="text-5xl mb-4">⚠️</p>
          <h2 className="text-xl font-bold text-slate-900 mb-2">
            Something went wrong
          </h2>
          <p className="text-slate-500 text-sm mb-6">
            {section} failed to load. This is usually a temporary issue.
          </p>
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.reload();
              }}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors"
            >
              Reload page
            </button>
            <Link
              to="/trending-ai"
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold rounded-lg transition-colors"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }
}
